/**
 * Domain 2: Quantitative Reasoning (التفكير الكمي والاستدلال الرياضي)
 * 8 Modular Template Families for mathematical, numerical, and relational logic
 */

import { wrapSvg, renderOptionSvg } from './svg-builder.js';

export const QUANTITATIVE_TEMPLATES = [
  // 1. Second-Order Difference Sequence
  {
    id: 'quant_arithmetic_second_order',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const start = prng.int(2, 8);
      const stepDiff = difficulty === 1 ? 2 : prng.int(2, 4); // Second difference
      let initialStep = prng.int(2, 5);

      const seq = [start];
      let curr = start;
      let currStep = initialStep;

      for (let i = 0; i < 4; i++) {
        curr += currStep;
        seq.push(curr);
        currStep += stepDiff;
      }

      // Next element
      const correctVal = curr + currStep;

      const distractors = [
        correctVal - stepDiff,
        correctVal + stepDiff,
        correctVal + 2 * stepDiff,
        correctVal - 1
      ].filter(v => v !== correctVal && !seq.includes(v)).slice(0, 3);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const seqDisplay = seq.join(' , ') + ' , [ ? ]';

      const stimulusSvg = `<svg class="iq-svg-math-series" viewBox="0 0 380 75" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="380" height="75" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255, 255, 255, 0.12)"/>
        <text x="190" y="46" fill="#f8fafc" font-family="system-ui, monospace" font-size="17" font-weight="700" text-anchor="middle">${seq.join('   ')}   ? </text>
      </svg>`;

      const options = allVals.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: String(val),
        text_en: String(val),
        value: val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو الرقم التالي الذي يكمل المتتابعة الحسابية المنطقية: \u2066${seqDisplay}\u2069؟`,
        questionText_en: `What is the next number that logically completes the sequence: ${seqDisplay}?`,
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `الفارق بين كل رقمين يزيد بمقدار ثابت (+${stepDiff}). الفارق الأخير كان ${currStep - stepDiff}، وبالتالي نضيف ${currStep} إلى ${curr} لنحصل على ${correctVal}.`,
        explanation_en: `The difference between successive terms grows by a constant step (+${stepDiff}). Adding ${currStep} to ${curr} yields ${correctVal}.`
      };
    }
  },

  // 2. Alternating Operations Series (e.g., *2, -3, *2, -3)
  {
    id: 'quant_geometric_alternating',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const mul = prng.pick([2, 3]);
      const sub = prng.int(2, 5);
      const start = prng.int(2, 6);

      const seq = [start];
      let curr = start;

      for (let i = 0; i < 4; i++) {
        if (i % 2 === 0) {
          curr *= mul;
        } else {
          curr -= sub;
        }
        seq.push(curr);
      }

      // Next is index 4 (5th transition)
      const correctVal = seq.length % 2 === 1 ? curr * mul : curr - sub;

      const cand = [
        correctVal + mul,
        correctVal + sub,
        correctVal + sub * 2,
        correctVal + mul * 2,
        correctVal * 2 + 1,
        correctVal > sub ? correctVal - sub : correctVal + 7,
        correctVal > mul ? correctVal - mul : correctVal + 9
      ].filter(v => v !== correctVal && v > 0);
      const distractors = Array.from(new Set(cand)).slice(0, 3);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const seqDisplay = seq.join(' , ') + ' , [ ? ]';
      const stimulusSvg = `<svg class="iq-svg-math-series" viewBox="0 0 380 75" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="380" height="75" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255, 255, 255, 0.12)"/>
        <text x="190" y="46" fill="#83C8F0" font-family="system-ui, monospace" font-size="17" font-weight="700" text-anchor="middle">${seq.join('   ')}   ? </text>
      </svg>`;

      const options = allVals.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: String(val),
        text_en: String(val),
        value: val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `استنتج الرقم التالي في المتتابعة المتناوبة: \u2066${seqDisplay}\u2069`,
        questionText_en: `Deduce the next number in the alternating sequence: ${seqDisplay}`,
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `تتناوب العمليات الحسابية بين (الضرب في ${mul}) ثم (طرح ${sub}). الخطوة المطلوبة تطبق عملية الضرب/الطرح لتنتج ${correctVal}.`,
        explanation_en: `The sequence alternates between multiplying by ${mul} and subtracting ${sub}, resulting in ${correctVal}.`
      };
    }
  },

  // 3. Generalized Additive Series (Lucas / Fibonacci variant)
  {
    id: 'quant_fibonacci_variant',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const a = prng.int(1, 4);
      const b = prng.int(a + 1, a + 4);

      const seq = [a, b];
      for (let i = 2; i < 5; i++) {
        seq.push(seq[i - 1] + seq[i - 2]);
      }

      const correctVal = seq[seq.length - 1] + seq[seq.length - 2];

      const cand = [
        correctVal - 2,
        correctVal + 3,
        correctVal + (seq[seq.length - 3] || 4),
        correctVal - seq[0],
        correctVal + 5,
        correctVal - 4
      ].filter(v => v !== correctVal && !seq.includes(v) && v > 0);
      const distractors = Array.from(new Set(cand)).slice(0, 3);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const seqDisplay = seq.join(' , ') + ' , [ ? ]';
      const stimulusSvg = `<svg class="iq-svg-math-series" viewBox="0 0 380 75" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="380" height="75" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255, 255, 255, 0.12)"/>
        <text x="190" y="46" fill="#f8fafc" font-family="system-ui, monospace" font-size="17" font-weight="700" text-anchor="middle">${seq.join('   ')}   ? </text>
      </svg>`;

      const options = allVals.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: String(val),
        text_en: String(val),
        value: val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو الحد التالي في المتتابعة الجمعية: \u2066${seqDisplay}\u2069؟`,
        questionText_en: `What is the next term in the additive sequence: ${seqDisplay}?`,
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `كل حد يساوي مجموع الحدين السابقين له مباشرة: ${seq[seq.length - 1]} + ${seq[seq.length - 2]} = ${correctVal}.`,
        explanation_en: `Every term is the exact sum of the two preceding terms: ${seq[seq.length - 1]} + ${seq[seq.length - 2]} = ${correctVal}.`
      };
    }
  },

  // 4. Number Triangle / Cross Sum Puzzle
  {
    id: 'quant_triangle_sum_puzzle',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      const ruleType = prng.pick([0, 1, 2, 3]);

      let evalCenter, expAr, expEn;
      if (ruleType === 0) {
        evalCenter = (top, left, right) => (top + left) * right;
        expAr = (t) => `القاعدة هي: (الرأس العلوي + الرأس الأيسر) × الرأس الأيمن = المركز. (${t.top} + ${t.left}) × ${t.right} = ${t.center}.`;
        expEn = (t) => `The rule is: (Top + Left) × Right = Center. (${t.top} + ${t.left}) × ${t.right} = ${t.center}.`;
      } else if (ruleType === 1) {
        evalCenter = (top, left, right) => (top * right) - left;
        expAr = (t) => `القاعدة هي: (الرأس العلوي × الرأس الأيمن) - الرأس الأيسر = المركز. (${t.top} × ${t.right}) - ${t.left} = ${t.center}.`;
        expEn = (t) => `The rule is: (Top × Right) - Left = Center. (${t.top} × ${t.right}) - ${t.left} = ${t.center}.`;
      } else if (ruleType === 2) {
        evalCenter = (top, left, right) => (left + right) * top;
        expAr = (t) => `القاعدة هي: (الرأس الأيسر + الرأس الأيمن) × الرأس العلوي = المركز. (${t.left} + ${t.right}) × ${t.top} = ${t.center}.`;
        expEn = (t) => `The rule is: (Left + Right) × Top = Center. (${t.left} + ${t.right}) × ${t.top} = ${t.center}.`;
      } else {
        evalCenter = (top, left, right) => top * left + right;
        expAr = (t) => `القاعدة هي: (الرأس العلوي × الرأس الأيسر) + الرأس الأيمن = المركز. (${t.top} × ${t.left}) + ${t.right} = ${t.center}.`;
        expEn = (t) => `The rule is: (Top × Left) + Right = Center. (${t.top} × ${t.left}) + ${t.right} = ${t.center}.`;
      }

      const makeTri = (top, left, right, isTarget = false) => {
        const center = evalCenter(top, left, right);
        return { top, left, right, center, isTarget };
      };

      const t1 = makeTri(prng.int(2, 5), prng.int(1, 4), prng.int(2, 4));
      const t2 = makeTri(prng.int(3, 6), prng.int(2, 5), prng.int(2, 4));
      const t3 = makeTri(prng.int(3, 7), prng.int(2, 6), prng.int(2, 4), true);

      const correctVal = t3.center;

      const distractorSet = new Set();
      const candidates = [correctVal - 3, correctVal + 4, correctVal + 2, correctVal - 4, correctVal + 6, correctVal - 2];
      for (const cand of candidates) {
        if (cand > 0 && cand !== correctVal && !distractorSet.has(cand)) {
          distractorSet.add(cand);
          if (distractorSet.size === 3) break;
        }
      }
      const distractors = Array.from(distractorSet);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const renderOneTri = (tri, cx, cy) => {
        const s = 36;
        const pts = `${cx},${cy - s} ${cx + s * 1.05},${cy + s * 0.7} ${cx - s * 1.05},${cy + s * 0.7}`;
        return `
          <polygon points="${pts}" fill="rgba(102, 102, 232, 0.12)" stroke="rgba(131, 200, 240, 0.4)" stroke-width="2"/>
          <circle cx="${cx}" cy="${cy + 6}" r="15" class="iq-svg-board" stroke="#3b82f6" stroke-width="1.5"/>
          <text x="${cx}" y="${cy - s - 6}" class="iq-svg-text" font-size="13" font-weight="bold" text-anchor="middle">${tri.top}</text>
          <text x="${cx - s * 1.05 - 9}" y="${cy + s * 0.7 + 5}" class="iq-svg-text" font-size="13" font-weight="bold" text-anchor="middle">${tri.left}</text>
          <text x="${cx + s * 1.05 + 9}" y="${cy + s * 0.7 + 5}" class="iq-svg-text" font-size="13" font-weight="bold" text-anchor="middle">${tri.right}</text>
          <text x="${cx}" y="${cy + 11}" fill="${tri.isTarget ? '#3b82f6' : 'currentColor'}" font-size="13" font-weight="800" text-anchor="middle">${tri.isTarget ? '?' : tri.center}</text>
        `;
      };

      const stimulusSvg = `<svg class="iq-svg-math-triangles" viewBox="0 0 360 120" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="360" height="120" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255, 255, 255, 0.12)"/>
        ${renderOneTri(t1, 65, 60)}
        ${renderOneTri(t2, 180, 60)}
        ${renderOneTri(t3, 295, 60)}
      </svg>`;

      const options = allVals.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: String(val),
        text_en: String(val),
        value: val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'الرقم الموجود في مركز كل مثلث يُحسب من أرقام رؤوسه وفق قاعدة رياضية ثابتة. ما هو الرقم الناقص؟',
        questionText_en: 'The number inside each triangle is derived from its vertex values by a consistent rule. What is the missing number?',
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: expAr(t3),
        explanation_en: expEn(t3)
      };
    }
  },

  // 5. Fraction & Halving Decay Series
  {
    id: 'quant_fraction_series',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const ruleType = prng.pick([0, 1, 2]);

      let fracSeq, correctVal, expAr, expEn, distractors;

      if (ruleType === 0) {
        const num = prng.pick([1, 3, 5]);
        const denoms = [2, 4, 8, 16];
        const correctDenom = 32;
        fracSeq = denoms.map(d => `${num}/${d}`);
        correctVal = `${num}/${correctDenom}`;
        distractors = [`${num}/${correctDenom - 8}`, `${num}/${correctDenom * 2}`, `${num + 1}/${correctDenom}`];
        expAr = `البسط يظل ثابتاً (${num})، بينما يتضاعف المقام في كل خطوة بالضرب في 2 (2، 4، 8، 16)، وبالتالي الحد التالي مقامه ${correctDenom}.`;
        expEn = `The numerator stays constant (${num}) while the denominator doubles at each step (2, 4, 8, 16), yielding ${correctVal}.`;
      } else if (ruleType === 1) {
        const num = prng.pick([1, 2]);
        const denoms = [3, 9, 27];
        const correctDenom = 81;
        fracSeq = denoms.map(d => `${num}/${d}`);
        correctVal = `${num}/${correctDenom}`;
        distractors = [`${num}/${correctDenom - 18}`, `${num}/${correctDenom + 27}`, `${num + 1}/${correctDenom}`];
        expAr = `البسط يظل ثابتاً (${num})، بينما يُضرب المقام في 3 في كل خطوة (3، 9، 27)، وبالتالي الحد التالي مقامه ${correctDenom}.`;
        expEn = `The numerator remains (${num}) while the denominator triples at each step (3, 9, 27), yielding ${correctVal}.`;
      } else {
        const num = prng.int(1, 4);
        const step = prng.pick([3, 4, 5]);
        const baseD = prng.int(2, 5);
        const denoms = [baseD, baseD + step, baseD + 2 * step, baseD + 3 * step];
        const correctDenom = baseD + 4 * step;
        fracSeq = denoms.map(d => `${num}/${d}`);
        correctVal = `${num}/${correctDenom}`;
        distractors = [`${num}/${correctDenom - step}`, `${num}/${correctDenom + step}`, `${num + 1}/${correctDenom}`];
        expAr = `البسط ثابت (${num})، بينما يزيد المقام بمقدار ثابت (+${step}) في كل خطوة، ما يجعل المقام التالي هو ${correctDenom}.`;
        expEn = `The numerator is constant (${num}) while the denominator increases by (+${step}) at each step, yielding ${correctVal}.`;
      }

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const stimulusSvg = `<svg class="iq-svg-math-series" viewBox="0 0 400 75" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="400" height="75" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255, 255, 255, 0.12)"/>
        <text x="200" y="46" fill="#83C8F0" font-family="system-ui, monospace" font-size="16" font-weight="700" text-anchor="middle">${fracSeq.join('  ,  ')}  ,  ?</text>
      </svg>`;

      const options = allVals.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: val,
        text_en: val,
        value: val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو الكسر التالي في هذه المتتابعة: ${fracSeq.join(' ، ')} ، [ ؟ ]؟`,
        questionText_en: `What is the next fraction in this progression: ${fracSeq.join(' , ')} , [ ? ]?`,
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: expAr,
        explanation_en: expEn
      };
    }
  },

  // 6. System of Symbolic Equations
  {
    id: 'quant_symbolic_equations',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      // Pool of 5 distinct geometric symbols with SVG renderers
      const symbolPool = [
        { name_ar: 'الدائرة الزرقاء', name_en: 'Blue Circle', render: (x, y) => `<circle cx="${x}" cy="${y}" r="10" fill="#83C8F0"/>` },
        { name_ar: 'المثلث البنفسجي', name_en: 'Purple Triangle', render: (x, y) => `<polygon points="${x},${y - 10} ${x + 10},${y + 8} ${x - 10},${y + 8}" fill="#6666E8"/>` },
        { name_ar: 'المربع الأخضر', name_en: 'Green Square', render: (x, y) => `<rect x="${x - 9}" y="${y - 9}" width="18" height="18" rx="3" fill="#10b981"/>` },
        { name_ar: 'النجمة الذهبية', name_en: 'Golden Star', render: (x, y) => `<polygon points="${x},${y - 10} ${x + 3},${y - 3} ${x + 10},${y - 3} ${x + 5},${y + 2} ${x + 7},${y + 9} ${x},${y + 5} ${x - 7},${y + 9} ${x - 5},${y + 2} ${x - 10},${y - 3} ${x - 3},${y - 3}" fill="#f59e0b"/>` },
        { name_ar: 'المعين الوردي', name_en: 'Pink Diamond', render: (x, y) => `<polygon points="${x},${y - 10} ${x + 10},${y} ${x},${y + 10} ${x - 10},${y}" fill="#ec4899"/>` }
      ];

      const chosenSymbols = prng.sample(symbolPool, 3);
      const symA = chosenSymbols[0];
      const symB = chosenSymbols[1];
      const symC = chosenSymbols[2];

      const a = prng.int(3, 9);
      const b = prng.int(2, 8);
      const c = prng.int(1, 6);

      const ruleType = prng.pick([0, 1, 2]);
      let eq1Svg, eq2Svg, eq3Svg, correctVal, expAr, expEn;

      if (ruleType === 0) {
        // Standard addition: A + A = 2a, A + B = a + b, A + B + C = ?
        const eq1Val = a + a;
        const eq2Val = a + b;
        correctVal = a + b + c;

        eq1Svg = `
          <g transform="translate(30, 25)">
            ${symA.render(0, 0)}
            <text x="22" y="5" fill="#f8fafc" font-size="16">+</text>
            ${symA.render(44, 0)}
            <text x="66" y="5" fill="#f8fafc" font-size="16">=</text>
            <text x="100" y="5" fill="#83C8F0" font-weight="bold" font-size="16">${eq1Val}</text>
          </g>`;
        eq2Svg = `
          <g transform="translate(30, 65)">
            ${symA.render(0, 0)}
            <text x="22" y="5" fill="#f8fafc" font-size="16">+</text>
            ${symB.render(44, 0)}
            <text x="66" y="5" fill="#f8fafc" font-size="16">=</text>
            <text x="100" y="5" fill="#83C8F0" font-weight="bold" font-size="16">${eq2Val}</text>
          </g>`;
        eq3Svg = `
          <g transform="translate(30, 105)">
            ${symA.render(0, 0)}
            <text x="22" y="5" fill="#f8fafc" font-size="16">+</text>
            ${symB.render(44, 0)}
            <text x="66" y="5" fill="#f8fafc" font-size="16">+</text>
            ${symC.render(88, 0)}
            <text x="110" y="5" fill="#f8fafc" font-size="16">=</text>
            <text x="140" y="5" fill="#83C8F0" font-weight="bold" font-size="18">?</text>
          </g>`;

        expAr = `من المعادلة الأولى: (${symA.name_ar}) = ${a}. من المعادلة الثانية: (${symB.name_ar}) = ${b}. بما أن (${symC.name_ar}) = ${c}، فإن الناتج = ${a} + ${b} + ${c} = ${correctVal}.`;
        expEn = `From equation 1: (${symA.name_en}) = ${a}. From equation 2: (${symB.name_en}) = ${b}. Given (${symC.name_en}) = ${c}, total = ${a} + ${b} + ${c} = ${correctVal}.`;
      } else if (ruleType === 1) {
        // Multiplicative precedence: A + A = 2a, A + 2B = a + 2b, B * A + C = ?
        const eq1Val = a + a;
        const eq2Val = a + 2 * b;
        correctVal = b * a + c;

        eq1Svg = `
          <g transform="translate(25, 25)">
            ${symA.render(0, 0)}
            <text x="20" y="5" fill="#f8fafc" font-size="16">+</text>
            ${symA.render(40, 0)}
            <text x="60" y="5" fill="#f8fafc" font-size="16">=</text>
            <text x="95" y="5" fill="#83C8F0" font-weight="bold" font-size="16">${eq1Val}</text>
          </g>`;
        eq2Svg = `
          <g transform="translate(25, 65)">
            ${symA.render(0, 0)}
            <text x="20" y="5" fill="#f8fafc" font-size="16">+</text>
            ${symB.render(40, 0)}
            <text x="60" y="5" fill="#f8fafc" font-size="16">+</text>
            ${symB.render(80, 0)}
            <text x="100" y="5" fill="#f8fafc" font-size="16">=</text>
            <text x="130" y="5" fill="#83C8F0" font-weight="bold" font-size="16">${eq2Val}</text>
          </g>`;
        eq3Svg = `
          <g transform="translate(25, 105)">
            ${symB.render(0, 0)}
            <text x="20" y="5" fill="#f8fafc" font-size="16">×</text>
            ${symA.render(40, 0)}
            <text x="60" y="5" fill="#f8fafc" font-size="16">+</text>
            ${symC.render(80, 0)}
            <text x="100" y="5" fill="#f8fafc" font-size="16">=</text>
            <text x="130" y="5" fill="#83C8F0" font-weight="bold" font-size="18">?</text>
          </g>`;

        expAr = `من الأولى: (${symA.name_ar}) = ${a}. من الثانية: 2 × (${symB.name_ar}) = ${2 * b} إذن الرمز الثاني = ${b}. في المعادلة الثالثة الضرب أولاً: (${b} × ${a}) + ${c} = ${correctVal}.`;
        expEn = `From eq 1: (${symA.name_en}) = ${a}. From eq 2: 2 × (${symB.name_en}) = ${2 * b}, so value is ${b}. Applying multiplication precedence in eq 3: (${b} × ${a}) + ${c} = ${correctVal}.`;
      } else {
        // Triple addition and subtraction
        const eq1Val = a * 3;
        const eq2Val = a + b;
        correctVal = (a + b) * 2 - c;

        eq1Svg = `
          <g transform="translate(20, 25)">
            ${symA.render(0, 0)}
            <text x="18" y="5" fill="#f8fafc" font-size="16">+</text>
            ${symA.render(36, 0)}
            <text x="54" y="5" fill="#f8fafc" font-size="16">+</text>
            ${symA.render(72, 0)}
            <text x="90" y="5" fill="#f8fafc" font-size="16">=</text>
            <text x="120" y="5" fill="#83C8F0" font-weight="bold" font-size="16">${eq1Val}</text>
          </g>`;
        eq2Svg = `
          <g transform="translate(20, 65)">
            ${symA.render(0, 0)}
            <text x="18" y="5" fill="#f8fafc" font-size="16">+</text>
            ${symB.render(36, 0)}
            <text x="54" y="5" fill="#f8fafc" font-size="16">=</text>
            <text x="85" y="5" fill="#83C8F0" font-weight="bold" font-size="16">${eq2Val}</text>
          </g>`;
        eq3Svg = `
          <g transform="translate(20, 105)">
            <text x="0" y="5" fill="#f8fafc" font-size="16">(</text>
            ${symA.render(16, 0)}
            <text x="32" y="5" fill="#f8fafc" font-size="16">+</text>
            ${symB.render(48, 0)}
            <text x="62" y="5" fill="#f8fafc" font-size="16">) × 2 -</text>
            ${symC.render(105, 0)}
            <text x="125" y="5" fill="#f8fafc" font-size="16">=</text>
            <text x="155" y="5" fill="#83C8F0" font-weight="bold" font-size="18">?</text>
          </g>`;

        expAr = `من الأولى: 3 × (${symA.name_ar}) = ${eq1Val} إذن الرمز الأول = ${a}. من الثانية: الرمز الثاني = ${b}. نطبق المعادلة الثالثة: (${a} + ${b}) × 2 - ${c} = ${correctVal}.`;
        expEn = `From eq 1: 3 × (${symA.name_en}) = ${eq1Val}, so first symbol = ${a}. From eq 2: second symbol = ${b}. Evaluating eq 3: (${a} + ${b}) × 2 - ${c} = ${correctVal}.`;
      }

      const distractorSet = new Set();
      const candidates = [correctVal - 2, correctVal + 3, correctVal + b + 1, correctVal - c - 1, correctVal + 4, correctVal - 3, correctVal + 2];
      for (const cand of candidates) {
        if (cand > 0 && cand !== correctVal && !distractorSet.has(cand)) {
          distractorSet.add(cand);
          if (distractorSet.size === 3) break;
        }
      }
      const distractors = Array.from(distractorSet);
      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const stimulusSvg = `<svg class="iq-svg-equations" viewBox="0 0 320 130" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="320" height="130" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255, 255, 255, 0.12)"/>
        ${eq1Svg}
        ${eq2Svg}
        ${eq3Svg}
      </svg>`;

      const options = allVals.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: String(val),
        text_en: String(val),
        value: val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا كانت قيمة (${symC.name_ar}) تساوي ${c}، فما هي القيمة العددية الصحيحة للمعادلة الثالثة؟`,
        questionText_en: `If the value of (${symC.name_en}) equals ${c}, what is the numerical value for the third equation?`,
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: expAr,
        explanation_en: expEn
      };
    }
  },

  // 7. Ratio and Proportion Balance Puzzle
  {
    id: 'quant_ratio_proportions',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const weightA = prng.int(2, 5); // Item A weight
      let weightB = prng.int(3, 7);   // Item B weight
      if (weightB === weightA) weightB = weightA + 2;

      const targetCount = prng.pick([2, 3, 4, 5]);
      const targetIsB = prng.next() > 0.4;
      const targetWeight = targetIsB ? weightB : weightA;
      const targetLabelAr = targetIsB ? 'مثلثات' : 'دوائر';
      const targetLabelEn = targetIsB ? 'Triangles' : 'Circles';

      const correctVal = targetCount * targetWeight;
      const distractorSet = new Set();
      const candidates = [correctVal - 3, correctVal + 4, correctVal + 2, correctVal - 2, correctVal + 5, correctVal - 4];
      for (const cand of candidates) {
        if (cand > 0 && cand !== correctVal && !distractorSet.has(cand)) {
          distractorSet.add(cand);
          if (distractorSet.size === 3) break;
        }
      }
      const distractors = Array.from(distractorSet);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const stimulusHtml_ar = `
        <div class="iq-balance-board" role="region" aria-label="أوزان الكتل">
          <div class="iq-balance-fact-row">
            <span class="iq-balance-item">▲ 1 مثلث = <strong>${weightB} كجم</strong></span>
            <span class="iq-balance-sep">|</span>
            <span class="iq-balance-item">● 1 دائرة = <strong>${weightA} كجم</strong></span>
          </div>
          <div class="iq-balance-target-row">
            <span class="iq-balance-target-lbl">الوزن الإجمالي لـ (${targetCount}) ${targetLabelAr} = [ ؟ ]</span>
          </div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-balance-board" role="region" aria-label="Block Weights">
          <div class="iq-balance-fact-row">
            <span class="iq-balance-item">▲ 1 Triangle = <strong>${weightB} kg</strong></span>
            <span class="iq-balance-sep">|</span>
            <span class="iq-balance-item">● 1 Circle = <strong>${weightA} kg</strong></span>
          </div>
          <div class="iq-balance-target-row">
            <span class="iq-balance-target-lbl">Total combined weight of (${targetCount}) ${targetLabelEn} = [ ? ]</span>
          </div>
        </div>
      `;

      const options = allVals.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: `${val} كجم`,
        text_en: `${val} kg`,
        value: val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا كانت كتلة المثلث الواحد ${weightB} كجم وكتلة الدائرة ${weightA} كجم، فكم يبلغ الوزن الإجمالي لـ ${targetCount} ${targetLabelAr}؟`,
        questionText_en: `If 1 triangle weighs ${weightB} kg and 1 circle weighs ${weightA} kg, what is the combined weight of ${targetCount} ${targetLabelEn}?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options,
        correctIndex,
        explanation_ar: `الوزن الإجمالي = ${targetCount} × ${targetWeight} = ${correctVal} كجم.`,
        explanation_en: `Total weight = ${targetCount} × ${targetWeight} = ${correctVal} kg.`
      };
    }
  },

  // 8. Modular / Cyclic Clock Arithmetic
  {
    id: 'quant_modulo_cyclic',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const baseHour = prng.int(7, 11);
      const addHours = prng.int(15, 38);

      // (baseHour + addHours - 1) % 12 + 1
      const total = baseHour + addHours;
      const correctHour = ((total - 1) % 12) + 1;

      const distractors = [
        ((correctHour + 2 - 1) % 12) + 1,
        ((correctHour + 6 - 1) % 12) + 1,
        ((correctHour - 3 + 11) % 12) + 1
      ].filter(h => h !== correctHour).slice(0, 3);

      const allHours = prng.shuffle([correctHour, ...distractors]);
      const correctIndex = allHours.indexOf(correctHour);

      const makeClockStim = (isEnglish) => `
        <div class="iq-clock-stimulus" role="region" aria-label="${isEnglish ? 'Cyclic Time Problem' : 'مسألة الوقت الدوري'}">
          <div class="iq-clock-dial-mini">
            <svg viewBox="0 0 70 70" width="56" height="56" xmlns="http://www.w3.org/2000/svg">
              <circle cx="35" cy="35" r="30" fill="rgba(59, 130, 246, 0.12)" stroke="#3b82f6" stroke-width="2.5"/>
              <line x1="35" y1="35" x2="35" y2="15" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
              <line x1="35" y1="35" x2="52" y2="35" stroke="#3b82f6" stroke-width="3" stroke-linecap="round"/>
              <circle cx="35" cy="35" r="3" fill="#3b82f6"/>
            </svg>
          </div>
          <div class="iq-clock-info">
            <div class="iq-clock-current">${isEnglish ? `Current Time: <strong>${baseHour}:00</strong>` : `الوقت الحالي: <strong>الساعة ${baseHour}:00</strong>`}</div>
            <div class="iq-clock-query">${isEnglish ? `After (${addHours}) hours: <strong>[ ? ]</strong>` : `بعد مرور (${addHours}) ساعة: <strong>[ ؟ ]</strong>`}</div>
          </div>
        </div>
      `;

      const options = allHours.map((h, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: `الساعة ${h}:00`,
        text_en: `${h}:00`,
        value: h
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا كانت الساعة تشير إلى ${baseHour}:00، فكم ستكون الساعة بعد مرور ${addHours} ساعة بالضبط؟`,
        questionText_en: `If a 12-hour clock currently shows ${baseHour}:00, what hour will it show in exactly ${addHours} hours?`,
        stimulusHtml: makeClockStim(false),
        stimulusHtml_ar: makeClockStim(false),
        stimulusHtml_en: makeClockStim(true),
        options,
        correctIndex,
        explanation_ar: `كل 12 ساعة تمثل دورة كاملة. بقسمة ${addHours} على 12 يتبقى ${addHours % 12} ساعة. ${baseHour} + ${addHours % 12} = ${correctHour}:00.`,
        explanation_en: `Every 12 hours forms a complete cycle. ${addHours} mod 12 = ${addHours % 12}. Adding ${addHours % 12} to ${baseHour} yields ${correctHour}:00.`
      };
    }
  }
];
